import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Star } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import Conviction from '../components/lens/Conviction'
import LensBand from '../components/lens/LensBand'
import LoginGate from '../components/common/LoginGate'
import UnscharfeIdeen from '../components/common/UnscharfeIdeen'
import { Fehler, Laden, Leer, SkeletonZeilen } from '../components/common/States'
import { useHealth, useJobs, useRegime, useStatus, useTeaser, useTopIdeas, useWatchlist } from '../hooks/useQualyz'
import { LENSES, datumVorTagen, formatDatum, formatDezimal, formatZahl } from '../lib/lenses'
import { regimeAnzeige } from '../lib/regime'
import { urteilVon, type Idea } from '../services/qualyz'

const WARTE_HINWEIS = 'Beim ersten Abruf nach einer Aktualisierung rechnen die Linsen bis zu 40 Sekunden.'
const RECHNET_TEXT = 'Die Linsen rechnen gerade neu. Gleich wieder da.'

function begruessung(stunde: number): string {
  if (stunde < 11) return 'Guten Morgen'
  if (stunde < 18) return 'Guten Tag'
  return 'Guten Abend'
}

export default function StartPage() {
  const { user, isAuthenticated, isAdmin, isLoading } = useAuth()
  const jetzt = new Date()
  const name = user?.first_name || user?.email?.split('@')[0] || ''
  const datum = jetzt.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="q-page">
      <section className="q-greet">
        <div>
          <h2 className="q-greet-title">
            {isAuthenticated ? (
              <>
                {begruessung(jetzt.getHours())}, <span className="q-accent">{name}</span>
              </>
            ) : (
              'Willkommen bei Qualyz'
            )}
          </h2>
          <p className="q-greet-date">{datum}</p>
        </div>
        {isAuthenticated ? (
          <Link to="/screener" className="q-btn q-btn-primary">
            Zum Screener <ArrowRight aria-hidden="true" />
          </Link>
        ) : null}
        {/* Nicht angemeldet: kein eigener Knopf hier -- "Anmelden" steht schon
            oben rechts und unter den Top-Ideen. Dreimal waere zu viel (Kim:
            weniger ist besser). */}
      </section>

      {/* Zwei Anzeigen nebeneinander: Zustand (traege, Trendlogik) und Breite
          (antizyklisch, an den Extremen). Zusammengemittelt wuerde genau die
          Spannung verschwinden, die interessant ist (Kim, 28.09.2026). */}
      <section className="q-hero q-hero--zwei">
        <RegimePanel />
        <BreitePanel />
      </section>

      <section className="q-hero q-hero--chance">
        <ChancePanel angemeldet={isAuthenticated} laedtAuth={isLoading} />
      </section>

      <StatusChips admin={isAdmin} />

      {isAuthenticated ? (
        <div className="q-cols">
          <TopKonviktion />
          <Favoriten />
        </div>
      ) : (
        !isLoading && <TeaserKonviktion />
      )}
    </div>
  )
}

/** Dauer im Nominativ: „45 Tage". */
function dauer(n: number): string {
  if (n >= 730) return `${formatDezimal(n / 365, 1)} Jahre`
  if (n >= 90) return `${Math.round(n / 30)} Monate`
  return `${n} ${n === 1 ? 'Tag' : 'Tage'}`
}

/** Dieselbe Dauer im Dativ, fuer „seit …": „45 Tagen". */
function dauerDativ(n: number): string {
  const w = dauer(n)
  return w.endsWith('Tag') ? `${w}` : `${w}n`
}

/**
 * Marktzustand: die Ampel, dazu wie lange die Phase schon laeuft und wie
 * lange solche Phasen ueblicherweise hielten.
 *
 * Die Phasendauer steht bewusst dabei, damit der Leser selbst einordnen kann
 * (Kim, 28.09.2026) -- NICHT als Faelligkeit. Gemessen (04.10.2026): Die
 * Wahrscheinlichkeit eines Rueckschlags haengt nicht davon ab, wie lange der
 * letzte her ist.
 */
function RegimePanel() {
  const regime = useRegime()
  const [zeigeTeile, setZeigeTeile] = useState(false)
  const anzeige = regime.data ? regimeAnzeige(regime.data.regime) : null
  const phasen = regime.data?.phasen
  const verlauf = regime.data?.verlauf
  const teile = regime.data?.regime.components

  return (
    <div className="q-panel">
      <div className="q-cap">Marktzustand</div>
      {regime.isPending ? (
        <div className="q-regime">
          <span className="q-skel" style={{ width: 180, height: 28 }} />
        </div>
      ) : regime.isError || !anzeige ? (
        <p className="q-regime-sub">Der Marktzustand ist gerade nicht verfügbar.</p>
      ) : (
        <>
          <div className={`q-regime q-regime--${anzeige.ton}`}>
            <span className="q-regime-dot" aria-hidden="true" />
            <span className="q-regime-label">{Math.round(regime.data!.regime.score_0_100)}</span>
            <span className="q-regime-von">von 100</span>
          </div>
          <div className="q-regime-sub">
            {anzeige.kurz} · {anzeige.phase} · {anzeige.empfehlung}
          </div>
          {verlauf && (verlauf.vortag || verlauf.vorwoche) ? (
            <p className="q-lage-verlauf">
              {([['gestern', verlauf.vortag], ['vor einer Woche', verlauf.vorwoche],
                 ['vor einem Monat', verlauf.vormonat]] as const)
                .filter(([, p]) => p)
                .map(([wann, p]) => (
                  <span key={wann} className="q-lage-schritt">
                    <em>{wann}</em>
                    <b>{Math.round(p!.wert)}</b>
                    <i className={p!.diff > 0 ? 'is-plus' : p!.diff < 0 ? 'is-minus' : undefined}>
                      {p!.diff > 0 ? '+' : ''}{formatDezimal(p!.diff, 1)}
                    </i>
                  </span>
                ))}
            </p>
          ) : null}
          {phasen?.median_tage ? (
            <p className="q-lage-zeile">
              Hält seit <strong>{dauerDativ(phasen.tage)}</strong>. Im Median hielten solche
              Phasen {dauer(phasen.median_tage)}, die längste{' '}
              {dauer(phasen.laengste_tage ?? phasen.median_tage)}.
            </p>
          ) : null}
          {teile ? (
            <>
              <button type="button" className="q-lage-mehr" onClick={() => setZeigeTeile((v) => !v)}>
                {zeigeTeile ? 'Bausteine ausblenden' : 'Woraus die Zahl entsteht'}
              </button>
              {zeigeTeile && <Bausteine teile={teile} />}
            </>
          ) : null}
        </>
      )}
    </div>
  )
}

const BAUSTEIN_NAMEN: Record<string, string> = {
  trend: 'Trend (S&P gegen 200-Tage-Linie)',
  credit_spread: 'Kreditaufschlag für schwache Schuldner',
  vix: 'Schwankungserwartung (VIX)',
  yield_curve: 'Zinskurve (10 Jahre minus 3 Monate)',
}
const BAUSTEIN_GEWICHT: Record<string, number> = {
  trend: 35, credit_spread: 25, vix: 20, yield_curve: 20,
}

function Bausteine({ teile }: { teile: Record<string, number> }) {
  return (
    <ul className="q-lage-teile">
      {Object.entries(BAUSTEIN_GEWICHT).map(([schluessel, gewicht]) => {
        const wert = teile[schluessel]
        if (wert === undefined) return null
        const anteil = Math.round(((wert + 1) / 2) * 100)
        return (
          <li key={schluessel}>
            <span className="q-lage-teil-name">
              {BAUSTEIN_NAMEN[schluessel] ?? schluessel} <em>{gewicht} %</em>
            </span>
            <span className="q-lage-balken" aria-hidden="true">
              <i style={{ width: `${anteil}%` }} className={wert < 0 ? 'is-minus' : undefined} />
            </span>
            <span className="q-lage-teil-wert">{wert > 0 ? '+' : ''}{wert.toFixed(2)}</span>
          </li>
        )
      })}
      <li className="q-lage-fuss">
        Der VIX zählt gegenläufig: Unter 15 gilt der Markt als auffällig ruhig, und
        auffällige Ruhe war historisch kein gutes Zeichen.
      </li>
    </ul>
  )
}

/**
 * Marktbreite: wie viele Titel tragen den Markt wirklich?
 *
 * KEIN erfundener Score -- die Breite IST schon eine Zahl von 0 bis 100. Und
 * sie liest sich nicht linear: Beide Enden gingen guten Renditen voraus, die
 * Mitte war am schwaechsten (gemessen 28.09.2026). Deshalb der Prozentwert
 * mit Einordnung statt einer Ampel.
 */
function BreitePanel() {
  const regime = useRegime()
  const s = regime.data?.stimmung
  const rhythmus = regime.data?.rhythmus

  return (
    <div className="q-panel">
      <div className="q-cap">Marktbreite</div>
      {regime.isPending ? (
        <div className="q-regime">
          <span className="q-skel" style={{ width: 180, height: 28 }} />
        </div>
      ) : !s ? (
        <p className="q-regime-sub">Die Marktbreite wird gerade berechnet.</p>
      ) : (
        <>
          <div className="q-regime">
            <span className="q-regime-label">{s.anteil.toFixed(0)} %</span>
            <span className="q-regime-von">der Titel über ihrer Jahreslinie</span>
          </div>
          <div className="q-regime-sub">
            {s.band} · im Jahresmittel {s.mittel_250.toFixed(0)} %
          </div>
          <p className="q-lage-zeile">{s.erklaerung}</p>

          {s.stufe > 0 ? (
            <div className={`q-warnung q-warnung--${s.stufe}`}>
              <strong>
                {s.stufe === 2 ? 'Bestätigt' : 'Auffällig'}
              </strong>{' '}
              {s.stufe === 2
                ? 'Der Markt stand oben, wurde schmal getragen — und fällt jetzt schnell.'
                : `Der Index steht ${formatDezimal(Math.abs(s.abstand_hoch_pct), 1)} % unter seinem Jahreshoch, getragen von weniger als der Hälfte der Titel.`}
              <span className="q-warnung-bilanz">
                {s.stufe === 2
                  ? `Dieser Fall trat seit 2015 dreimal ein — jedes Mal folgten 17 bis 25 % Rückgang. Drei Fälle sind wenig.`
                  : `Dieses Muster gab es seit 2015 elfmal. Dreimal folgte ein größerer Rückschlag, siebenmal nichts.`}
              </span>
            </div>
          ) : null}

          {rhythmus?.korrektur ? (
            <p className="q-lage-zeile">
              Rückschläge von 10 % kamen etwa alle{' '}
              {formatDezimal(rhythmus.korrektur.alle_x_jahre, 1)} Jahre, von 20 % alle{' '}
              {rhythmus.baerenmarkt ? formatDezimal(rhythmus.baerenmarkt.alle_x_jahre, 1) : '—'}{' '}
              Jahre. Der letzte 10-Prozent-Rückgang begann vor{' '}
              {formatDezimal(rhythmus.korrektur.her_jahre, 1)} Jahren. Daraus folgt nichts
              über morgen: Gemessen hängt die Wahrscheinlichkeit nicht davon ab, wie lange
              der letzte her ist.
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}

/**
 * Die hoechste Konviktion des Tages -- ergaenzt das Regime um den konkreten Titel.
 * Besucher sehen sie auch: Sie ist der erste der drei Titel im Ausschnitt.
 */
function ChancePanel({ angemeldet, laedtAuth }: { angemeldet: boolean; laedtAuth: boolean }) {
  const top = useTopIdeas(20)
  const teaser = useTeaser()
  const regime = useRegime()

  if (laedtAuth) return <div className="q-panel" />

  const abruf = angemeldet ? top : teaser
  const chance: Idea | undefined = angemeldet ? top.data?.[0] : teaser.data?.ideas[0]
  const rechnet = !angemeldet && teaser.data?.wird_berechnet === true

  return (
    <div className="q-panel">
      <div className="q-cap">Chance des Tages</div>
      {abruf.isPending ? (
        <Laden text="Die Linsen rechnen …" hinweis={angemeldet ? WARTE_HINWEIS : undefined} kompakt />
      ) : abruf.isError ? (
        <Fehler fehler={abruf.error} nochmal={() => abruf.refetch()} kompakt />
      ) : !chance ? (
        <Leer text={rechnet ? RECHNET_TEXT : 'Heute erfüllt kein Titel die Datengrundlage für eine Empfehlung.'} kompakt />
      ) : (
        <>
          <div className="q-chance-top">
            <Conviction wert={chance.conviction} von={chance.lenses_total} groesse="lg" />
            <Link className="q-titlelink q-chance-who" to={`/aktie/${encodeURIComponent(chance.symbol)}`}>
              <div className="q-sym q-sym--lg">{chance.symbol}</div>
              <div className="q-name">{chance.name}</div>
            </Link>
            <LensBand idea={chance} groesse="sm" />
          </div>
          <p className="q-chance-why">{begruendung(chance, regime.data?.regime.color)}</p>
        </>
      )}
    </div>
  )
}

function begruendung(idea: Idea, regimeFarbe: string | undefined) {
  const stark = LENSES.filter((l) => urteilVon(idea, l.key).urteil === 'STRONG').map((l) => l.label)
  const traegtRegime =
    regimeFarbe === 'green' &&
    (urteilVon(idea, 'momentum').urteil === 'STRONG' || urteilVon(idea, 'sentiment').urteil === 'STRONG')
  return (
    <>
      <b>
        {idea.conviction} von {idea.lenses_total} Linsen
      </b>{' '}
      mit Kaufsignal
      {stark.length > 0 && (
        <>
          , am stärksten bei <b>{stark.slice(0, 3).join(', ')}</b>
        </>
      )}
      .{traegtRegime && ' Passt zum Risk-On-Regime.'}
    </>
  )
}

function StatusChips({ admin }: { admin: boolean }) {
  const status = useStatus()
  const health = useHealth()
  const jobs = useJobs()

  const tageAlt = health.data?.daten?.kurse_tage_alt
  const kurse = status.data?.kurse_stand ?? (typeof tageAlt === 'number' ? datumVorTagen(tageAlt) : null)
  const veraltet = status.data?.kurse_veraltet ?? health.data?.daten?.veraltet ?? false

  // Vergleichsgruppe und Rückvergleich sind Innenleben des Modells -- Admin-Sicht
  // wie die Modell-Seiten. Der Abo-Status ist für Kunden irrelevant (Kim, 12.09.2026).
  const chips: { k: string; v: string; ton?: string }[] = []
  if (typeof status.data?.universum === 'number') chips.push({ k: 'Universum', v: formatZahl(status.data.universum) })
  if (admin && typeof status.data?.vergleichsgruppe === 'number')
    chips.push({ k: 'Vergleichsgruppe', v: formatZahl(status.data.vergleichsgruppe) })
  if (kurse) chips.push({ k: 'Kurse', v: formatDatum(kurse), ton: veraltet ? 'is-warn' : 'is-ok' })
  // Seit dem Ende des Datenabos (25.09.2026) muss sichtbar sein, wie alt die
  // Zahlen sind -- nicht nur die Kurse (Kim, 28.09.2026).
  const stand = status.data?.datenstand
  if (stand?.abschluesse?.stand)
    chips.push({
      k: 'Abschlüsse',
      v: formatDatum(stand.abschluesse.stand),
      ton: stand.abschluesse.veraltet ? 'is-warn' : 'is-ok',
    })
  if (stand?.profile?.stand)
    chips.push({
      k: 'Profile',
      v: formatDatum(stand.profile.stand),
      ton: stand.profile.veraltet ? 'is-warn' : 'is-ok',
    })
  if (admin && status.data?.rueckvergleich)
    chips.push({ k: 'Rückvergleich', v: `${status.data.rueckvergleich.stichtage} Stichtage`, ton: 'is-acc' })
  if (admin && jobs.data)
    chips.push({ k: 'Datenabo', v: jobs.data.abo_aktiv ? 'aktiv' : 'pausiert', ton: jobs.data.abo_aktiv ? 'is-ok' : 'is-warn' })

  if (chips.length === 0) return null
  return (
    <section aria-label="Datenstand">
      <div className="q-chips">
        {chips.map((c) => (
          <div key={c.k} className="q-chip">
            <div className="q-chip-k">{c.k}</div>
            <div className={`q-chip-v${c.ton ? ` ${c.ton}` : ''}`}>{c.v}</div>
          </div>
        ))}
      </div>
      <p className="q-chips-fuss">
        {stand?.hinweis
          ? stand.hinweis
          : 'Kurse kommen täglich; Abschlüsse und Profile stammen aus dem letzten Abruf.'}
      </p>
    </section>
  )
}

function IdeenZeile({ idea }: { idea: Idea }) {
  return (
    <div className="q-list-row">
      <Conviction wert={idea.conviction} von={idea.lenses_total} />
      <Link className="q-titlelink" to={`/aktie/${encodeURIComponent(idea.symbol)}`}>
        <div className="q-titlecell">
          <span className="q-sym">{idea.symbol}</span>
          <span className="q-name">{idea.name}</span>
        </div>
      </Link>
      <LensBand idea={idea} groesse="sm" />
      <span className="q-list-meta" title="Durchschnitt der urteilenden Linsen">
        Ø {formatDezimal(idea.avg_score, 0)}
      </span>
    </div>
  )
}

function TopKonviktion() {
  const top = useTopIdeas(20)

  return (
    <section className="q-card">
      <div className="q-card-head">
        <h3 className="q-card-title">Top-Konviktion heute</h3>
        <Link to="/screener" className="q-card-link">
          Alle im Screener →
        </Link>
      </div>
      {top.isPending ? (
        <SkeletonZeilen anzahl={5} />
      ) : top.isError ? (
        <Fehler fehler={top.error} nochmal={() => top.refetch()} />
      ) : (top.data ?? []).length === 0 ? (
        <Leer text="Heute keine Ideen mit ausreichender Datengrundlage." />
      ) : (
        (top.data ?? []).slice(0, 5).map((idea) => <IdeenZeile key={idea.symbol} idea={idea} />)
      )}
    </section>
  )
}

/**
 * Besucher: drei echte Ideen, der Rest unscharf hinter der Anmeldung (Kim, 13.09.2026).
 * Die unscharfen Zeilen sind Attrappen -- das Backend liefert Besuchern nur die drei.
 */
function TeaserKonviktion() {
  const teaser = useTeaser()
  const ideen = teaser.data?.ideas ?? []

  return (
    <section className="q-card">
      <div className="q-card-head">
        <h3 className="q-card-title">Top-Konviktion heute</h3>
      </div>
      {teaser.isPending ? (
        <SkeletonZeilen anzahl={3} />
      ) : teaser.isError ? (
        <Fehler fehler={teaser.error} nochmal={() => teaser.refetch()} />
      ) : ideen.length === 0 ? (
        <Leer
          text={teaser.data?.wird_berechnet ? RECHNET_TEXT : 'Heute keine Ideen mit ausreichender Datengrundlage.'}
        />
      ) : (
        ideen.map((idea) => <IdeenZeile key={idea.symbol} idea={idea} />)
      )}
      <LoginGate titel="Alle Ideen nach der Anmeldung" vorschau={<UnscharfeIdeen anzahl={4} />} />
    </section>
  )
}

function Favoriten() {
  const watchlist = useWatchlist()
  const top = useTopIdeas(20)
  const bekannt = new Map((top.data ?? []).map((i) => [i.symbol, i]))
  const eintraege = watchlist.data?.items ?? []

  return (
    <section className="q-card">
      <div className="q-card-head">
        <h3 className="q-card-title">Deine Favoriten</h3>
        <Link to="/watchlist" className="q-card-link">
          Watchlist →
        </Link>
      </div>
      {watchlist.isPending ? (
        <SkeletonZeilen anzahl={3} />
      ) : watchlist.isError ? (
        <Fehler fehler={watchlist.error} nochmal={() => watchlist.refetch()} />
      ) : eintraege.length === 0 ? (
        <Leer text="Noch keine Favoriten. Im Screener mit dem Stern merken." />
      ) : (
        eintraege.slice(0, 5).map((eintrag) => {
          const idea = bekannt.get(eintrag.symbol)
          return (
            <div key={eintrag.symbol} className="q-list-row q-list-row--fav">
              <span className="q-fav-star" aria-hidden="true">
                <Star fill="currentColor" />
              </span>
              <Link className="q-titlelink" to={`/aktie/${encodeURIComponent(eintrag.symbol)}`}>
                <div className="q-titlecell">
                  <span className="q-sym">{eintrag.symbol}</span>
                  <span className="q-name">{eintrag.name}</span>
                </div>
              </Link>
              {idea ? <Conviction wert={idea.conviction} von={idea.lenses_total} /> : <span />}
            </div>
          )
        })
      )}
    </section>
  )
}
